import { useState } from "react";
import { Link } from "react-router-dom";
import { AdminShell } from "@/components/admin/AdminShell";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Loader2, Play, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

type Check = { name: string; ok: boolean; detail?: string };
type Report = { ok: boolean; total: number; passed: number; failed: number; checks: Check[]; utm: any; cleaned_up: boolean };

export default function AdminAttributionTest() {
  const [running, setRunning] = useState(false);
  const [cleanup, setCleanup] = useState(true);
  const [report, setReport] = useState<Report | null>(null);

  async function run() {
    setRunning(true); setReport(null);
    try {
      const { data, error } = await supabase.functions.invoke("attribution-e2e-test", { body: { cleanup } });
      if (error || (data as any)?.error) throw new Error(error?.message ?? (data as any)?.error);
      setReport(data as Report);
      (data as Report).ok ? toast.success(`All ${(data as Report).total} checks passed`)
        : toast.error(`${(data as Report).failed} of ${(data as Report).total} failed`);
    } catch (e) { toast.error((e as Error).message); }
    finally { setRunning(false); }
  }

  return (
    <AdminShell title="Attribution E2E Test" actions={
      <Link to="/admin/attribution-test/history" className="text-xs underline">View run history →</Link>
    }>
      <p className="text-muted-foreground mb-4 max-w-2xl text-sm">
        Generates a fresh UTM tuple, fires a landing-page view, captures a lead, creates a
        synthetic booking, redeems a synthetic promo, and verifies that the first-touch
        UTM persists at every hop and that the promo-analytics aggregation reports the
        view and the redemption.
      </p>
      <div className="bg-panel border border-border p-4 mb-6 flex flex-wrap items-center gap-4">
        <Button onClick={run} disabled={running} className="gap-2">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Run
        </Button>
        <div className="flex items-center gap-2 text-sm">
          <Switch checked={cleanup} onCheckedChange={setCleanup} />
          <span>Clean up synthetic rows after run</span>
        </div>
        {report && <Badge variant={report.ok ? "default" : "destructive"} className="ml-auto">{report.passed}/{report.total}</Badge>}
      </div>
      {report && (
        <>
          <div className="bg-surface border border-border p-4 mb-4">
            <h4 className="font-display tracking-widest text-xs mb-2">GENERATED UTM</h4>
            <pre className="text-[11px] font-mono overflow-x-auto">{JSON.stringify(report.utm, null, 2)}</pre>
          </div>
          <div className="bg-surface border border-border p-4">
            <ul className="space-y-2">
              {report.checks.map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  {c.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5" /> : <XCircle className="w-4 h-4 text-destructive mt-0.5" />}
                  <div>
                    <div>{c.name}</div>
                    {c.detail && <div className="text-[11px] text-muted-foreground font-mono">{c.detail}</div>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </AdminShell>
  );
}